'use client';

import { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/context/AuthContext';

type UserRole = 'broker' | 'admin';

interface UserContextIndicatorProps {
  userName?: string;
  brokerageName?: string;
  userRole?: UserRole;
  onLogout?: () => void;
}

const UserContextIndicator = ({
  userName = 'James Mitchell',
  brokerageName = 'Pinnacle Pension Advisors',
  userRole = 'broker',
  onLogout,
}: UserContextIndicatorProps) => {
  const { logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const initials = userName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const roleLabel = userRole === 'admin' ? 'QRSL Administrator' : 'Pension Broker';

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label={`User menu for ${userName}`}
        className="flex items-center gap-2.5 px-2 py-1.5 rounded-md hover:bg-white/10 transition-all duration-[250ms] ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-1 focus-visible:ring-offset-primary min-h-[44px]"
      >
        {/* Avatar */}
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-accent text-white font-caption font-500 text-sm flex-shrink-0">
          {initials}
        </div>
        {/* Name - hidden on small screens */}
        <div className="hidden lg:block text-left">
          <p className="text-white text-sm font-medium leading-tight truncate max-w-[140px]">{userName}</p>
          <p className="text-white/60 text-xs font-caption leading-tight truncate max-w-[140px]">{brokerageName}</p>
        </div>
        <Icon
          name="ChevronDownIcon"
          size={16}
          variant="outline"
          className={`text-white/60 hidden lg:block transition-transform duration-[250ms] ease-out ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-72 bg-card rounded-md shadow-elevation-4 border border-border z-[50] overflow-hidden">
          {/* User info header */}
          <div className="px-4 py-4 bg-muted border-b border-border">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-11 h-11 rounded-full bg-primary text-white font-caption font-500 text-base flex-shrink-0">
                {initials}
              </div>
              <div className="min-w-0">
                <p className="text-text-primary font-medium text-sm truncate">{userName}</p>
                <p className="text-text-secondary text-xs font-caption truncate">{brokerageName}</p>
                <div className="flex items-center gap-1 mt-1">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-caption font-500 ${
                    userRole === 'admin' ?'bg-primary/10 text-primary' :'bg-secondary/10 text-secondary'
                  }`}>
                    <Icon name={userRole === 'admin' ? 'ShieldCheckIcon' : 'BriefcaseIcon'} size={11} variant="solid" />
                    {roleLabel}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Menu items */}
          <div className="py-1">
            <Link
              href={userRole === 'admin' ? '/admin-control-center' : '/broker-dashboard'}
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-3 px-4 py-3 text-sm text-text-primary hover:bg-muted transition-all duration-[250ms] ease-out min-h-[44px]"
            >
              <Icon name="UserCircleIcon" size={18} variant="outline" className="text-text-secondary" />
              <span>My Profile</span>
            </Link>
            <Link
              href={userRole === 'admin' ? '/admin-control-center' : '/broker-dashboard'}
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-3 px-4 py-3 text-sm text-text-primary hover:bg-muted transition-all duration-[250ms] ease-out min-h-[44px]"
            >
              <Icon name="Cog6ToothIcon" size={18} variant="outline" className="text-text-secondary" />
              <span>Settings</span>
            </Link>
            <div className="flex items-center gap-3 px-4 py-3 min-h-[44px]">
              <Icon name="LockClosedIcon" size={18} variant="outline" className="text-success" />
              <div>
                <p className="text-xs font-caption text-text-secondary">Session Encrypted</p>
                <p className="text-xs font-data text-success">AES-256 · TLS 1.3</p>
              </div>
            </div>
          </div>

          <div className="border-t border-border py-1">
            <button
              onClick={() => {
                setIsOpen(false);
                logout();
                onLogout?.();
              }}
              className="flex items-center gap-3 w-full px-4 py-3 text-sm text-error hover:bg-error/5 transition-all duration-[250ms] ease-out min-h-[44px]"
            >
              <Icon name="ArrowRightOnRectangleIcon" size={18} variant="outline" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserContextIndicator;